/**
 * Default run columns and detail renderers for ModelOutput-based tables.
 *
 * Shared between HintDetail, SubmissionDetail, and any future operation
 * detail page that displays model run outputs.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { RunsTableColumn } from './RunsTable';
import { formatCost, formatLatency, type PromptLookup } from './promptUtils';
import type { ReturnLocation } from '../navigation';

export interface BuildRunColumnsOptions<T = ModelOutputRow> {
    /** Outgoing nav from this table (to the prompt editor, to the model-run
     *  detail page) stamps this on router state so the destination's
     *  BackButton can read its label. Pass the current page's identity
     *  (e.g. `{ label: 'Back to Hint Detail' }`). */
    returnTo?: ReturnLocation;
    /** Optional per-row context cell (e.g. the question range an enrichment
     *  batch covered — "Q1–Q4"). When supplied, an "Info" column is rendered;
     *  when omitted, no Info column appears, so pages that don't need it are
     *  unaffected. The page owns what goes here. */
    info?: (row: T) => ReactNode;
}

/** Compact arrow icon used by the prompt-name cell to indicate a link. */
function LinkIcon({ title }: { title: string }) {
    return (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <title>{title}</title>
            <path d="M7 17L17 7" />
            <path d="M8 7h9v9" />
        </svg>
    );
}

/** Prompt name + small "open prompt editor" icon link. */
function PromptNameCell({ promptId, name, isPrimary, returnTo }: {
    promptId: string;
    name: string;
    isPrimary: boolean;
    returnTo?: ReturnLocation;
}) {
    return (
        <div className="prompt-cell">
            <span className="prompt-name">
                {name}{isPrimary ? ' (PRIMARY)' : ''}
            </span>
            <Link
                to={`/prompts?selected=${promptId}`}
                state={returnTo ? { returnTo } : undefined}
                className="prompt-link-icon"
                title="Open prompt editor"
                onClick={e => e.stopPropagation()}
                style={{ marginLeft: 6, verticalAlign: 'middle', display: 'inline-flex' }}
            >
                <LinkIcon title="Open prompt editor" />
            </Link>
        </div>
    );
}

/**
 * Model cell — shows the MODEL that served the call (`params.model`), falling
 * back to the provider key only when no model was recorded. The provider key is
 * in the tooltip.
 *
 * Adds a tier badge when the call went through a tiered provider set: without
 * it two rows naming the same model are indistinguishable, which is exactly the
 * case a tiered set produces. Plus the "⮑ failover" badge when a backup
 * provider served the call.
 */
/** What the Model cell names. Exported so the column's sort key is the same
 *  value the reader sees rather than a second, drifting derivation. */
export function modelName(run: Pick<ModelOutputRow, 'parameters'>): string {
    return run.parameters.model || run.parameters.provider;
}

/** When the call started, in epoch ms. Prefers the persisted `startedAt` (real
 *  dispatch time) and falls back to `generatedAt − latencyMs` for older rows
 *  that predate the field. Null when neither is parseable, which sorts the row
 *  last rather than to 1970.
 *
 *  The fallback misreads concurrent calls — several rows written together share
 *  one `generatedAt`, so subtracting each row's own latency can order a call
 *  before another that truly ran earlier. `startedAt` removes that ambiguity. */
export function startedAt(run: { startedAt?: string; generatedAt?: string; latencyMs?: number }): number | null {
    if (run.startedAt) {
        const s = Date.parse(run.startedAt);
        if (!Number.isNaN(s)) return s;
    }
    if (!run.generatedAt) return null;
    const end = Date.parse(run.generatedAt);
    return Number.isNaN(end) ? null : end - (run.latencyMs ?? 0);
}

/** A call that asked for a media resolution above the default, marked beside
 *  the model — with whether it could have applied. gemini-2.5 reads every image
 *  at 258 tokens on a call with several images, whatever was asked. */
function MediaResolutionBadge({ params, imageTokens }: { params: ModelOutputRow['parameters']; imageTokens?: number }) {
    const requested = params.actual?.family?.mediaResolution;
    if (!requested) return null;
    const level = requested.replace('MEDIA_RESOLUTION_', '');
    const images = params.actual?.family?.imageCount;
    const notApplied = (images ?? 0) > 1 && /gemini-2\.5/.test(params.model ?? params.provider);
    const title = `Media resolution ${level} requested`
        + (typeof images === 'number' ? ` · ${images} image${images === 1 ? '' : 's'}` : '')
        + (typeof imageTokens === 'number' ? ` · ${imageTokens.toLocaleString()} image tokens` : '')
        + (notApplied ? '\nNot applied: gemini-2.5 reads each image at 258 tokens on a call with several images.' : '');
    return (
        <span className={`provider-tier-badge${notApplied ? ' is-muted' : ''}`} title={title}>
            {level}{notApplied ? ' ✕' : ''}
        </span>
    );
}

export function ProviderCell({ params, imageTokens }: { params: ModelOutputRow['parameters']; imageTokens?: number }) {
    const served = params.model || params.provider;
    const resolution = <MediaResolutionBadge params={params} imageTokens={imageTokens} />;
    const tier = params.actual?.tierRouting;
    const tierBadge = tier?.finalTier ? (
        <span
            className="provider-tier-badge"
            title={`Tiered set${tier.providerSet ? ` "${tier.providerSet}"` : ''} — served at ${tier.finalTier}`}
        >
            {tier.finalTier}
        </span>
    ) : null;
    const nameTag = <span className="provider-tag" title={`provider: ${params.provider}`}>{served}</span>;
    const requested = params.requestedProvider;
    const failed = params.failedProviders ?? [];
    const isFailover = (!!requested && requested !== params.provider) || failed.length > 0;
    if (!isFailover) {
        return tierBadge || params.actual?.family?.mediaResolution
            ? <span className="provider-cell-failover">{nameTag}{tierBadge}{resolution}</span>
            : nameTag;
    }
    const chain = failed.length > 0
        ? failed.map(f => `${f.key} (${f.error})`).join('  →  ')
        : requested;
    return (
        <span className="provider-cell-failover">
            {nameTag}
            {tierBadge}
            {resolution}
            <span
                className="provider-failover-badge"
                title={`Failover — served by backup provider.\nRequested: ${requested ?? '(unknown)'}\nFailed: ${chain}`}
            >
                ⮑ failover
            </span>
        </span>
    );
}

/**
 * When the call STARTED, as mm:ss.
 *
 * Prefers the persisted `startedAt` (real dispatch time); older rows fall back
 * to `generatedAt − latencyMs`. Ordering by the completion write-time misreads
 * concurrent calls — a slow call that started first can land last — which is
 * exactly what the `startedAt` field fixes. Minute and second only; the date is
 * the run's own and repeating it on every row buys nothing.
 */
export function StartTimeCell(
    { startedAt: startedAtIso, generatedAt, latencyMs }:
    { startedAt?: string; generatedAt?: string; latencyMs?: number },
) {
    const started = startedAt({ startedAt: startedAtIso, generatedAt, latencyMs });
    if (started == null) return <span className="run-time-cell muted">-</span>;
    const start = new Date(started);
    const mm = String(start.getMinutes()).padStart(2, '0');
    const ss = String(start.getSeconds()).padStart(2, '0');
    const finished = new Date(started + (latencyMs ?? 0));
    return (
        <span className="run-time-cell" title={`Started ${start.toLocaleString()} · finished ${finished.toLocaleTimeString()}`}>
            {mm}:{ss}
        </span>
    );
}

/** Minimal shape needed by the default columns. Any object with these fields works. */
export interface ModelOutputRow {
    id: string;
    parameters: {
        promptId: string;
        provider: string;
        model?: string;
        /** The originally-requested provider, present only when cross-provider
         *  failover served the call from a different (backup) provider. */
        requestedProvider?: string;
        /** Providers tried and failed before the served one (failover chain). */
        failedProviders?: { key: string; error: string }[];
        /** Resolved call params. `tierRouting` is present only when the call was
         *  served through a TIERED provider set — the tier is what distinguishes
         *  two rows that name the same model. */
        actual?: {
            tierRouting?: { finalTier?: string; providerSet?: string; escalations?: unknown[] };
            /** Adapter-specific params as sent. Only the media-resolution fields
             *  are read here. */
            family?: { kind?: string; mediaResolution?: string; imageCount?: number };
        };
    };
    isPrimary: boolean;
    /** When the call actually STARTED (real dispatch time), ISO. The reliable
     *  ordering key; absent on rows written before it existed. */
    startedAt?: string;
    /** When the row was written — i.e. when the call FINISHED. Used as the
     *  fallback start (`generatedAt − latencyMs`) only when `startedAt` is
     *  absent; that fallback misreads concurrent calls, which `startedAt` fixes. */
    generatedAt?: string;
    latencyMs: number;
    cost: number;
    tokenUsage?: { total?: number; image?: number };
    errors?: string[];
    /** Structured error records — new rows persist only these (no legacy `errors[]`). */
    errorRecords?: { code: string; stage?: string; provider?: string; providerCode?: string; message?: string }[];
    /** Why generation stopped. A truncation here ('max_tokens' / 'length') is a
     *  FAILED run even though nothing threw — see `deriveRunStatus`. */
    stopReason?: string;
    providerStopReason?: string;
    /** `'skipped'` marks a step the pipeline declined to run — no model was
     *  called, so cost/latency/tokens are zero and there is no output to read.
     *  `'failover_attempt'` marks a provider call that failed and was replaced
     *  by a backup: a real, billed call whose row exists so its raw response and
     *  token spend survive. The step itself still succeeded. */
    /** In-flight bookkeeping on a `'running'` row (model output lifecycle):
     *  per-attempt timeout, failover hop, and when that hop started. */
    timeoutMs?: number;
    hop?: number;
    hopStartedAt?: string;
    status?: string;
    /** Why the step was skipped. Set with `status: 'skipped'`. */
    skipReason?: string;
    /** Which step a skipped row stands in for (a skip has no prompt to name it). */
    stepType?: string;
}

/** A run's terminal error, or `null` when it succeeded. `code` is the short
 *  label shown in a cell (e.g. `max_tokens`); `title` is the full string for a
 *  hover tooltip (e.g. `[max_tokens] Unterminated string in JSON…`). */
export interface RunStatus {
    code: string;
    title: string;
}

/**
 * Derive a run's status. New rows persist structured `errorRecords` (the legacy
 * free-form `errors[]` is written just as a fallback), so read the last record's
 * `code` first and fall back to `errors`. Returns `null` for a clean run.
 *
 * A TRUNCATED run counts as failed even though nothing threw: the provider
 * returned 200 and the pipeline completed, so no error was recorded, but the
 * output stops mid-structure and whatever consumed it saw a partial document.
 * Reporting that as "success" is how a mark scheme that lost its last nine
 * questions looked fine in this table while silently under-marking students.
 */
export function deriveRunStatus(
    // stop reasons are optional so existing call sites (and tests) that pass only
    // the error fields still typecheck.
    run: Pick<ModelOutputRow, 'errors' | 'errorRecords' | 'stopReason' | 'providerStopReason'>,
): RunStatus | null {
    const records = run.errorRecords;
    const last = records?.[records.length - 1];
    if (last?.code) {
        const msg = run.errors?.length ? run.errors.join(' | ') : (last.message ?? last.code);
        return { code: last.code, title: `[${last.code}] ${msg}` };
    }
    if (run.errors?.length) {
        return { code: 'error', title: run.errors.join(' | ') };
    }
    // Provider-agnostic truncation signals. Anthropic/OpenAI say `max_tokens` /
    // `length`; Gemini says `MAX_TOKENS`. Match loosely so a new provider's
    // spelling doesn't silently read as success.
    const stop = `${run.stopReason ?? ''} ${run.providerStopReason ?? ''}`.toLowerCase();
    if (/max_tokens|maxtokens|\blength\b/.test(stop)) {
        return {
            code: 'truncated',
            title: `Output truncated — the model hit its token ceiling (stopReason: ${run.stopReason ?? run.providerStopReason}). `
                + 'The response is incomplete; raise maxTokens for this run.',
        };
    }
    return null;
}

/**
 * Status cell — a green "success" for a clean run, or the short error code as a
 * red pill whose native `title` hover reveals the full error string. Inline
 * styles (like `ErrorRecordChips`) so it renders identically wherever it's used,
 * with or without the operation-detail stylesheet.
 */
/** Grace past a hop's timeout before a running row counts as orphaned —
 *  the backend's `ORPHAN_GRACE_MS` (modelrun/outputState.ts). */
const ORPHAN_GRACE_MS = 2 * 60 * 1000;

/**
 * Whether a row is a call still in flight, and how long it has been going.
 * Mirrors the backend's `outputState`: a stored `'orphaned'`, or a `'running'`
 * row past its hop's timeout plus grace, is orphaned. Null for a finished row.
 */
export function runLiveState(
    run: Pick<ModelOutputRow, 'status'> & Partial<Pick<ModelOutputRow, 'startedAt' | 'hopStartedAt' | 'timeoutMs' | 'parameters'>>,
    nowMs: number = Date.now(),
): { state: 'running' | 'orphaned'; elapsed: string; provider: string } | null {
    if (run.status !== 'running' && run.status !== 'orphaned') return null;
    const startMs = run.startedAt ? new Date(run.startedAt).getTime() : nowMs;
    const hopMs = run.hopStartedAt ? new Date(run.hopStartedAt).getTime() : startMs;
    const overdue = run.timeoutMs !== undefined && nowMs > hopMs + run.timeoutMs + ORPHAN_GRACE_MS;
    const secs = Math.max(0, Math.round((nowMs - startMs) / 1000));
    const elapsed = secs >= 90 ? `${Math.floor(secs / 60)}m ${secs % 60}s` : `${secs}s`;
    return {
        state: run.status === 'orphaned' || overdue ? 'orphaned' : 'running',
        elapsed,
        provider: run.parameters?.provider ?? '',
    };
}

export function RunStatusCell({ run }: {
    run: Pick<ModelOutputRow,
        'errors' | 'errorRecords' | 'stopReason' | 'providerStopReason' | 'status' | 'skipReason'>
        & Partial<Pick<ModelOutputRow, 'startedAt' | 'hopStartedAt' | 'timeoutMs' | 'hop' | 'parameters'>>;
}) {
    // A running call's "running · Ns" counts on its own, once a second. The list
    // re-fetches every few seconds, but an unchanged response keeps the same
    // data, so the row did not re-render and the time sat still between polls.
    const [, tick] = useState(0);
    const isRunning = run.status === 'running';
    useEffect(() => {
        if (!isRunning) return;
        const t = setInterval(() => tick(n => n + 1), 1000);
        return () => clearInterval(t);
    }, [isRunning]);

    // A skipped step is neither success nor failure — no model was called. Amber
    // so it reads as "nothing happened here", distinct from the green of a run
    // that produced output and the red of one that broke.
    if (run.status === 'skipped') {
        return (
            <span
                title={run.skipReason ?? 'Step skipped — no model was called.'}
                style={{
                    display: 'inline-block', maxWidth: 160,
                    padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                    border: '1px solid rgba(245, 158, 11, 0.35)', background: 'rgba(245, 158, 11, 0.10)',
                    color: '#f59e0b', cursor: 'help',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    verticalAlign: 'middle',
                }}
            >
                skipped
            </span>
        );
    }
    // A failed attempt that a backup then covered. Red would say "this operation
    // broke" when it did not — the step succeeded on another provider. Amber,
    // and named for what happened, with the real error code kept in the tooltip.
    if (run.status === 'failover_attempt') {
        const failed = deriveRunStatus(run);
        return (
            <span
                title={failed
                    ? `${failed.title}\n\nA backup provider served this call — the step still succeeded.`
                    : 'This provider failed; a backup served the call.'}
                style={{
                    display: 'inline-block', maxWidth: 160,
                    padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                    border: '1px solid rgba(245, 158, 11, 0.35)', background: 'rgba(245, 158, 11, 0.10)',
                    color: '#f59e0b', cursor: 'help',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    verticalAlign: 'middle',
                }}
            >
                failed over{failed ? ` · ${failed.code}` : ''}
            </span>
        );
    }
    // A call still in flight — its row exists from the call's start. Blue while
    // it is inside its timeout; grey once it is past it, because the process
    // running it is gone (PipelineInfra/model-output-lifecycle-target.md).
    const live = runLiveState(run);
    if (live) {
        const orphaned = live.state === 'orphaned';
        const colour = orphaned ? '#9ca3af' : '#3b82f6';
        return (
            <span
                title={orphaned
                    ? `Still marked running ${live.elapsed} after it started on ${live.provider} — the process running it is gone. Nothing was recorded from it.`
                    : `Running on ${live.provider}${run.hop ? ` (failover hop ${run.hop})` : ''} for ${live.elapsed}${run.timeoutMs ? `, of a ${Math.round(run.timeoutMs / 1000)}s timeout` : ''}.`}
                style={{
                    display: 'inline-block', maxWidth: 160,
                    padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                    border: `1px solid ${colour}59`, background: `${colour}1a`,
                    color: colour, cursor: 'help',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    verticalAlign: 'middle',
                }}
            >
                {orphaned ? 'orphaned' : `running · ${live.elapsed}`}
            </span>
        );
    }
    const status = deriveRunStatus(run);
    if (!status) {
        return <span style={{ color: '#22c55e', fontSize: 12 }}>success</span>;
    }
    return (
        <span
            title={status.title}
            style={{
                display: 'inline-block', maxWidth: 160,
                padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                border: '1px solid rgba(239, 68, 68, 0.35)', background: 'rgba(239, 68, 68, 0.10)',
                color: '#ef4444', cursor: 'help',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                verticalAlign: 'middle',
            }}
        >
            {status.code}
        </span>
    );
}

/** Stable identity for each shared run column. Consumers compose by NAME
 *  (never by array position) so reordering can't silently break a call site. */
export type RunColumnKey = 'time' | 'prompt' | 'type' | 'info' | 'provider' | 'latency' | 'cost' | 'tokens' | 'output' | 'status';

/** Canonical left-to-right order for the default columns — the single source
 *  of truth for the sequence. `buildDefaultRunColumns` renders this order. The
 *  'info' column is only included when the caller supplies an `info` accessor. */
export const DEFAULT_RUN_COLUMN_ORDER: readonly RunColumnKey[] = [
    // Time leads: the table's rows are not otherwise ordered in any way the
    // reader can see, and "which call ran first" is the first question asked of
    // a multi-call run (failover, tier escalation, parallel branches).
    'time', 'prompt', 'type', 'info', 'provider', 'latency', 'cost', 'tokens', 'output', 'status',
];

/**
 * Build the shared run columns as a NAME-keyed map. Pick the columns you need
 * by key and arrange them explicitly at the call site (e.g. to splice in
 * page-specific columns) — never index into an array by position.
 * Call inside the component (needs prompt lookup from the hook).
 */
export function buildRunColumnMap<T extends ModelOutputRow>(
    lookup: PromptLookup,
    options?: BuildRunColumnsOptions<T>,
): Record<RunColumnKey, RunsTableColumn<T>> {
    const returnTo = options?.returnTo;
    return {
        time: {
            header: 'Time',
            className: 'run-time-col',
            render: r => <StartTimeCell startedAt={r.startedAt} generatedAt={r.generatedAt} latencyMs={r.latencyMs} />,
            // The full start instant, not the mm:ss the cell shows — minutes
            // and seconds alone reorder wrongly across an hour boundary.
            sortValue: r => startedAt(r),
        },
        prompt: {
            header: 'Prompt',
            render: r => (
                <PromptNameCell
                    promptId={r.parameters.promptId}
                    name={lookup.getPromptName(r.parameters.promptId)}
                    isPrimary={r.isPrimary}
                    returnTo={returnTo}
                />
            ),
        },
        type: {
            header: 'Type',
            render: r => <span className="prompt-type-badge">{lookup.getPromptType(r.parameters.promptId) || '-'}</span>,
            sortValue: r => lookup.getPromptType(r.parameters.promptId) || null,
        },
        info: {
            header: 'Info',
            render: r => options?.info ? options.info(r) : null,
        },
        provider: {
            // "Model", not "Provider": the cell renders params.model and only
            // falls back to the provider key. Calling it Provider is what made
            // "gemini-2.5-flash" read as a provider name.
            header: 'Model',
            render: r => <ProviderCell params={r.parameters} imageTokens={r.tokenUsage?.image} />,
            sortValue: r => modelName(r),
        },
        latency: {
            header: 'Latency',
            render: r => formatLatency(r.latencyMs),
            sortValue: r => r.latencyMs,
        },
        cost: {
            header: 'Cost',
            className: 'cost',
            render: r => formatCost(r.cost),
            sortValue: r => r.cost,
        },
        tokens: {
            header: 'Tokens',
            render: r => r.tokenUsage?.total?.toLocaleString() ?? '-',
        },
        output: {
            header: 'Output',
            render: r => (
                <Link
                    to={`/model-data/${r.id}`}
                    state={returnTo ? { returnTo } : undefined}
                    className="model-output-link"
                    onClick={e => e.stopPropagation()}
                    title="Open Model Run detail"
                >
                    {r.id.slice(-6)}
                    <span style={{ marginLeft: 4, verticalAlign: 'middle', display: 'inline-flex' }}>
                        <LinkIcon title="Open Model Run detail" />
                    </span>
                </Link>
            ),
        },
        status: {
            header: 'Status',
            render: r => <RunStatusCell run={r} />,
        },
    };
}

/**
 * The shared run columns in the canonical default order. Consumers that need a
 * custom arrangement should use `buildRunColumnMap` and pick columns by name.
 */
export function buildDefaultRunColumns<T extends ModelOutputRow>(
    lookup: PromptLookup,
    options?: BuildRunColumnsOptions<T>,
): RunsTableColumn<T>[] {
    const map = buildRunColumnMap<T>(lookup, options);
    // The Info column only earns its space when the page supplies an accessor.
    const order = options?.info
        ? DEFAULT_RUN_COLUMN_ORDER
        : DEFAULT_RUN_COLUMN_ORDER.filter(key => key !== 'info');
    return order.map(key => map[key]);
}

/**
 * Build the standard heading for a selected run's detail card.
 */
export function buildRunDetailHeading(
    run: ModelOutputRow,
    lookup: PromptLookup,
): ReactNode {
    const promptType = lookup.getPromptType(run.parameters.promptId);
    return <>
        {lookup.getPromptName(run.parameters.promptId)}
        <ProviderCell params={run.parameters} imageTokens={run.tokenUsage?.image} />
        {run.isPrimary && <span className="primary-badge">Primary</span>}
        {promptType && (
            <span className="prompt-type-badge" style={{ marginLeft: 8 }}>
                {promptType}
            </span>
        )}
    </>;
}

/**
 * Build the standard summary for a selected run's detail card.
 */
export function buildRunSummary(run: ModelOutputRow): ReactNode {
    return (
        <div className="metadata-grid" style={{ marginBottom: 12 }}>
            <div className="meta-item">
                <label>Latency</label>
                <span>{formatLatency(run.latencyMs)}</span>
            </div>
            <div className="meta-item">
                <label>Cost</label>
                <span>{formatCost(run.cost)}</span>
            </div>
            <div className="meta-item">
                <label>Tokens</label>
                <span>{run.tokenUsage?.total?.toLocaleString() ?? '-'}</span>
            </div>
        </div>
    );
}

/**
 * Extract the error banner string from a run (for RunDetailCard's error prop).
 * New rows persist only `errorRecords` (no legacy `errors[]`), so fall back to
 * the last record's message — otherwise the banner silently disappears. Leads
 * with the structured error code (e.g. `[max_tokens]`).
 */
export function getRunError(run: ModelOutputRow): string | undefined {
    const records = run.errorRecords;
    const last = records?.[records.length - 1];
    const msg = run.errors?.length ? run.errors.join(' | ') : last?.message;
    if (!msg) return undefined;
    return last?.code ? `[${last.code}] ${msg}` : msg;
}
